import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { ApiError } from '../../lib/apiError';
import { useSignIn } from './queries';

/**
 * Anmeldemaske.
 *
 * Die Pruefregeln sind aus denselben Regeln abgeleitet wie die
 * Pydantic-Modelle im Backend (docs/02, Abschnitt 4). Sie ersparen dem
 * Anwender den Weg zum Server -- massgeblich ist bei einer Abweichung immer
 * das Backend.
 *
 * Ausdruecklich **nicht** geprueft wird hier die Kennwortrichtlinie. Beim
 * Anmelden gilt das Kennwort, das vergeben wurde; eine Laengenpruefung an
 * dieser Stelle wuerde nur einen Nutzer aussperren, dessen Kennwort aus einer
 * Zeit vor der heutigen Richtlinie stammt.
 *
 * **Der zweite Faktor wird nachgefragt und nicht vorweg abgefragt** (F5-16).
 * Ob eine Kennung einen zweiten Faktor traegt, sagt der Server erst, wenn das
 * Kennwort gestimmt hat -- ein Codefeld, das von Anfang an dasteht, waere
 * harmlos, aber die Maske frage damit nach etwas, das die meisten Nutzer
 * nicht haben. Der Server antwortet mit ``MFA_REQUIRED``; erst dann erscheint
 * das Feld.
 *
 * **``MFA_REQUIRED`` ist kein Fehler.** Der Aufruf ist gelungen, das Kennwort
 * hat gestimmt, und es fehlt nur noch ein Schritt. Ueber ``ErrorNotice``
 * gemeldet, las sich das als "Es ist ein Fehler aufgetreten" samt
 * Fehlerkennung -- und wer das nicht zu Ende liest, tippt Kennung und
 * Kennwort noch einmal ein, weil er annimmt, sich vertan zu haben. Genau
 * dafuer hat der Server einen eigenen Code (``core/errors.py``,
 * ``MfaRequiredError``: "damit die Oberflaeche nach dem Code fragen kann
 * statt eine fehlgeschlagene Anmeldung zu melden"). ``SignInNotice`` unten
 * loest das ein.
 */
const schema = z.object({
  email: z.string().min(1, 'auth.emailRequired').email('auth.emailInvalid'),
  password: z.string().min(1, 'auth.passwordRequired'),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const { t } = useTranslation();
  const signIn = useSignIn();
  const [totpCode, setTotpCode] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  // Der Server hat das Kennwort angenommen und verlangt den zweiten Faktor --
  // entweder weil noch keiner kam, oder weil der eingegebene nicht stimmte.
  // In allen drei Faellen bleibt das Codefeld stehen: Wer sich beim Abtippen
  // vertan hat, soll es noch einmal versuchen koennen, ohne die ganze Maske
  // neu auszufuellen.
  const needsCode =
    signIn.error instanceof ApiError &&
    ['MFA_REQUIRED', 'MFA_CODE_INVALID', 'MFA_CODE_USED'].includes(signIn.error.code);

  const onSubmit = handleSubmit((values) => {
    signIn.mutate({ ...values, ...(totpCode ? { totpCode } : {}) });
  });

  return (
    <div className="centered-page">
      <div className="card centered-page__card">
        <h1>{t('auth.signInTitle')}</h1>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-6)' }}>
          {t('auth.signInSubtitle')}
        </p>

        <SignInNotice error={signIn.isError ? signIn.error : null} />

        <form onSubmit={onSubmit} noValidate>
          <TextField
            label={t('auth.email')}
            type="email"
            autoComplete="username"
            autoFocus
            error={errors.email ? t(errors.email.message ?? '') : undefined}
            {...register('email')}
          />
          <TextField
            label={t('auth.password')}
            type="password"
            autoComplete="current-password"
            error={errors.password ? t(errors.password.message ?? '') : undefined}
            {...register('password')}
          />
          {needsCode || totpCode ? (
            <TextField
              label={t('auth.totpCode')}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              hint={t('auth.totpHint')}
              value={totpCode}
              onChange={(event) => {
                setTotpCode(event.target.value);
              }}
            />
          ) : null}

          <Button type="submit" variant="primary" block busy={signIn.isPending}>
            {signIn.isPending ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>
      </div>
    </div>
  );
}

/**
 * Die Meldung ueber der Anmeldemaske.
 *
 * Drei Faelle, drei Toene -- und der Unterschied ist nicht Geschmack, sondern
 * die Frage, was der Anwender als Naechstes tun soll:
 *
 * | Antwort des Servers | Ton | Weil |
 * |---|---|---|
 * | ``MFA_REQUIRED`` | Hinweis, blau | Nichts ist schiefgegangen. Es fehlt ein Schritt. |
 * | ``MFA_CODE_INVALID``, ``MFA_CODE_USED`` | Warnung, gelb | Der Code passt nicht -- eine Eingabe, die sich wiederholen laesst. |
 * | alles Uebrige | ``ErrorNotice``, rot | Ein Fehler, ueber den zu berichten sich lohnt. |
 *
 * **Die Fehlerkennung erscheint in den ersten beiden Faellen nicht.** Sie ist
 * dafuer da, einen Vorgang im Serverprotokoll wiederzufinden. Bei einem
 * vertippten Code gibt es nichts wiederzufinden -- die Kennung waere eine
 * Zumutung, die aussieht, als sei etwas kaputt.
 */
function SignInNotice({ error }: { error: unknown }) {
  const { t } = useTranslation();

  if (!(error instanceof ApiError)) {
    return error ? <ErrorNotice error={error} /> : null;
  }

  if (error.code === 'MFA_REQUIRED') {
    // `role="status"` und nicht `alert`: Ein vorlesendes Programm soll den
    // Satz einschieben, wenn es an der Reihe ist, und nicht die Eingabe
    // unterbrechen. Unterbrochen wird bei Fehlern, nicht bei Schritten.
    return (
      <div className="notice notice--info" role="status">
        <span className="notice__title">{t('auth.mfaStepTitle')}</span>
        <span>{t('auth.mfaStepBody')}</span>
      </div>
    );
  }

  if (error.code === 'MFA_CODE_INVALID' || error.code === 'MFA_CODE_USED') {
    return (
      <div className="notice notice--warning" role="alert">
        <span>{t(`errors.${error.code}`)}</span>
      </div>
    );
  }

  return <ErrorNotice error={error} />;
}
